import { useEffect, useState } from 'react'
import { Menu } from '@mui/material'
import { CheckIcon, PlusIcon, XIcon } from '@phosphor-icons/react'
import { FormProvider, useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'

import { CloseIcon } from '@assets/svgs/CloseIcon'
import { DatePicker } from '@components/shared/DatePicker'
import { Select } from '@components/shared/Select'

import { PreviewManagementFilters } from '../../../PreviewManagementFilters'

import { useUser } from '@hooks/useUser'

import * as apiAccessProfile from '@services/api/accessProfile'
import * as apiPreview from '@services/api/preview'
import * as apiUsers from '@services/api/users'

import type { ExtraFilterField, HistoryFiltersProps, PreviewHistoryExtraFiltersFormProps } from './types'

import * as S from './styles'

const STORAGE_KEY = 'previewHistory.extraFilterFields'

const initialExtraValues: PreviewHistoryExtraFiltersFormProps = {
  weekNumber: '',
  status: '',
  isAutoSend: '',
  responsibleUserId: '',
  userProfile: '',
  startDate: '',
  endDate: ''
}

// Ordem fixa de exibição no grid, independente da ordem em que o usuário adicionou
const EXTRA_FIELDS: { id: ExtraFilterField; label: string }[] = [
  { id: 'weekNumber', label: 'Semana' },
  { id: 'status', label: 'Status' },
  { id: 'isAutoSend', label: 'Envio Automático' },
  { id: 'responsibleUserId', label: 'Usuário' },
  { id: 'userProfile', label: 'Perfil' },
  { id: 'startDate', label: 'Data Início' },
  { id: 'endDate', label: 'Data Fim' }
]

const ALL_OPTION = { label: 'Todos', value: 'all' }

const SELECT_OPTIONS: Partial<Record<ExtraFilterField, { label: string; value: string }[]>> = {
  weekNumber: [ALL_OPTION, ...[1, 2, 3, 4, 5].map((week) => ({ label: `Semana ${week}`, value: String(week) }))],
  isAutoSend: [
    ALL_OPTION,
    { label: 'Sim', value: 'true' },
    { label: 'Não', value: 'false' }
  ]
}

// Campos extras escolhidos ficam salvos no navegador (preferência do usuário)
const loadSavedFields = (): ExtraFilterField[] => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]')
    return EXTRA_FIELDS.map((field) => field.id).filter((id) => Array.isArray(saved) && saved.includes(id))
  } catch {
    return []
  }
}

const USER_SEARCH_DELAY = 400

const saveFields = (fields: ExtraFilterField[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fields))
  } catch {
    // sem storage (aba anônima/bloqueado) a preferência só vale para a sessão
  }
}

export const HistoryFilters = ({ initialBaseFilters, onFilter }: HistoryFiltersProps) => {
  const [addedFields, setAddedFields] = useState<ExtraFilterField[]>(loadSavedFields)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [lastBaseFilters, setLastBaseFilters] = useState<Parameters<HistoryFiltersProps['onFilter']>[0] | null>(null)

  const extraMethods = useForm<PreviewHistoryExtraFiltersFormProps>({ defaultValues: initialExtraValues })

  const [userSearch, setUserSearch] = useState('')
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('')
  const [knownUsers, setKnownUsers] = useState<Record<string, string>>({})

  const { filiData } = useUser()
  const hasStatusField = addedFields.includes('status')
  const hasUserField = addedFields.includes('responsibleUserId')
  const hasProfileField = addedFields.includes('userProfile')
  const selectedUserId = extraMethods.watch('responsibleUserId')

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedUserSearch(userSearch), USER_SEARCH_DELAY)
    return () => clearTimeout(timeout)
  }, [userSearch])

  // Mesma lista de status do filtro do detalhamento da tela de Prévia; o code vai no payload
  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['preview-history-status', filiData?.filiCode],
    queryFn: () => apiPreview.getConsolidatedPreviewFilters({ branchCodeFilter: [filiData!.filiCode] }),
    enabled: hasStatusField && !!filiData?.filiCode
  })

  const { data: profilesData, isLoading: profilesLoading } = useQuery({
    queryKey: ['preview-history-profiles'],
    queryFn: apiAccessProfile.getAllProfiles,
    enabled: hasProfileField
  })

  // Busca por parte do nome no back; o "code" do usuário é o responsibleUserId do histórico
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['preview-history-users', debouncedUserSearch],
    queryFn: () => apiUsers.getUsers({ bodyParams: { userNameFilter: debouncedUserSearch || null, onlyActive: true } }),
    enabled: hasUserField
  })

  const foundUsers: { code: number; name: string }[] = (usersData?.items ?? []).filter(
    (user: { code?: number; name?: string }) => user.code != null && !!user.name
  )

  useEffect(() => {
    if (!foundUsers.length) return

    setKnownUsers((prev) => ({ ...prev, ...Object.fromEntries(foundUsers.map((user) => [String(user.code), user.name])) }))
  }, [usersData])

  const userOptions = foundUsers.map((user) => ({ label: user.name, value: String(user.code) }))

  // Mantém o usuário escolhido na lista mesmo quando a busca seguinte não o devolve (senão o campo fica em branco)
  if (selectedUserId && selectedUserId !== 'all' && !userOptions.some((option) => option.value === selectedUserId)) {
    userOptions.unshift({ label: knownUsers[selectedUserId] ?? selectedUserId, value: selectedUserId })
  }

  // O back já devolve um "Todos" na lista: descarta o dele e usa o ALL_OPTION ('all'), que o payload ignora
  const statusOptions = [
    ALL_OPTION,
    ...(statusData?.status ?? [])
      .filter((item) => !['all', 'todos'].includes(item.code?.toLowerCase()) && item.name?.toLowerCase() !== 'todos')
      .map((item) => ({ label: item.name, value: item.code }))
  ]

  const profileOptions = [
    ALL_OPTION,
    ...(profilesData ?? []).map((profile) => ({ label: profile.name ?? profile.description ?? profile.code, value: profile.code }))
  ]

  const updateFields = (fields: ExtraFilterField[]) => {
    const ordered = EXTRA_FIELDS.map((field) => field.id).filter((id) => fields.includes(id))
    setAddedFields(ordered)
    saveFields(ordered)
  }

  const handleRemoveField = (id: ExtraFilterField) => {
    updateFields(addedFields.filter((field) => field !== id))
    extraMethods.setValue(id, initialExtraValues[id])
    if (lastBaseFilters) onFilter(lastBaseFilters, extraMethods.getValues())
  }

  return (
    <>
      <S.Toolbar>
        <S.AddFilterButton type="button" onClick={(event) => setMenuAnchor(event.currentTarget)}>
          <PlusIcon size={12} weight="bold" />
          Adicionar filtro
        </S.AddFilterButton>
      </S.Toolbar>

      {/* Fica aberto para marcar vários campos de uma vez; clicar em um já marcado remove o filtro */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { component: S.MenuPaper }, list: { component: S.MenuList } }}
      >
        <S.CloseIconBox>
          <S.CloseIcon onClick={() => setMenuAnchor(null)}>
            <CloseIcon />
          </S.CloseIcon>
        </S.CloseIconBox>

        <S.MenuItemBox>
          {EXTRA_FIELDS.map((field) => {
            const isAdded = addedFields.includes(field.id)

            return (
              <S.MenuItem
                key={field.id}
                disabled={false}
                onClick={() => (isAdded ? handleRemoveField(field.id) : updateFields([...addedFields, field.id]))}
              >
                <S.MenuItemLabel>{field.label}</S.MenuItemLabel>
                {isAdded && <CheckIcon size={16} weight="bold" />}
              </S.MenuItem>
            )
          })}
        </S.MenuItemBox>
      </Menu>

      <PreviewManagementFilters
        initialValues={initialBaseFilters}
        onFilter={(baseFilters) => {
          setLastBaseFilters(baseFilters)
          onFilter(baseFilters, extraMethods.getValues())
        }}
        onClear={() => extraMethods.reset(initialExtraValues)}
      >
        <FormProvider {...extraMethods}>
          {EXTRA_FIELDS.filter((field) => addedFields.includes(field.id)).map((field) => (
            <S.ExtraField key={field.id}>
              <S.RemoveFieldButton
                type="button"
                aria-label={`Remover filtro ${field.label}`}
                onClick={() => handleRemoveField(field.id)}
              >
                <XIcon size={12} weight="bold" />
              </S.RemoveFieldButton>

              {field.id === 'startDate' || field.id === 'endDate' ? (
                <DatePicker name={field.id} label={field.label} placeholder="DD/MM/AAAA" hasIcon />
              ) : (
                <Select
                  name={field.id}
                  label={field.label}
                  placeholder="Todos"
                  options={
                    field.id === 'responsibleUserId'
                      ? [ALL_OPTION, ...userOptions]
                      : field.id === 'userProfile'
                        ? profileOptions
                        : field.id === 'status'
                          ? statusOptions
                          : (SELECT_OPTIONS[field.id] ?? [])
                  }
                  loading={
                    (field.id === 'responsibleUserId' && usersLoading) ||
                    (field.id === 'userProfile' && profilesLoading) ||
                    (field.id === 'status' && statusLoading)
                  }
                  searchable={field.id === 'responsibleUserId' || field.id === 'userProfile'}
                  searchPlaceholder={field.id === 'responsibleUserId' ? 'Buscar usuário...' : 'Buscar perfil...'}
                  onSearchChange={field.id === 'responsibleUserId' ? setUserSearch : undefined}
                />
              )}
            </S.ExtraField>
          ))}
        </FormProvider>
      </PreviewManagementFilters>
    </>
  )
}
